/**
 * v4 shell - bilingual, professional. Bottom-tab nav for mobile, sticky
 * top bar with business identity + accountant + settings.
 */

import { Outlet, NavLink, useLocation, Link } from 'react-router-dom';
import { Home, FolderOpen, Briefcase, MessageCircle, Settings as SettingsIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { V4SettingsProvider, useV4 } from './i18n';
import SplashV4 from './Splash';
import InstallPrompt from './InstallPrompt';
import PinGate from './PinGate';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useClient, listMessages, isAuthenticated } from '@/services/api/client-auth';
import { setAppBadge } from '@/lib/appBadge';

const navItems = [
  { to: '/client',       labelKey: 'nav_today', icon: Home,          end: true },
  { to: '/client/ask',   labelKey: 'nav_ask',   icon: MessageCircle },
  { to: '/client/money', labelKey: 'nav_money', icon: Briefcase },
  { to: '/client/files', labelKey: 'nav_files', icon: FolderOpen },
];

function Shell() {
  const { t, dir, font, settings, themeMode, accent } = useV4();
  const loggedInClient = useClient();
  const clientName = loggedInClient?.displayName;
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const waitingRef = useRef<ServiceWorker | null>(null);

  // Keep the installed-app icon badge in sync with unread messages while the
  // app is open (the SW handles it while closed, via the push payload).
  const { data: msgData } = useQuery({
    queryKey: ['client-unread-badge'],
    queryFn: () => listMessages(1),
    enabled: isAuthenticated(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  useEffect(() => {
    setAppBadge(msgData?.unreadCount ?? 0);
  }, [msgData?.unreadCount]);

  // Register service worker for push notifications + offline support
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/sw.js').then(reg => {
      // Detect a waiting worker (update ready) on register
      if (reg.waiting) { waitingRef.current = reg.waiting; setUpdateAvailable(true); }
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) {
            waitingRef.current = nw;
            setUpdateAvailable(true);
          }
        });
      });
    }).catch(e => console.warn('SW register failed:', e));

    // Auto-reload when a new SW takes control
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  }, []);

  // Online/offline tracking
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const applyUpdate = () => {
    if (waitingRef.current) waitingRef.current.postMessage({ type: 'SKIP_WAITING' });
  };
  const location = useLocation();

  // Override the <html> theme class to match v4's setting while mounted.
  // Tailwind dark variants are triggered by any ancestor .dark, so we MUST
  // set the html class - applying .dark to the v4 root alone isn't enough
  // when the firm panel has already added .dark to <html>. On unmount,
  // restore whatever the firm panel had so it isn't left in the wrong mode.
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.classList.contains('dark') ? 'dark' : 'light';
    html.classList.remove('light', 'dark');
    html.classList.add(themeMode);
    return () => {
      html.classList.remove('light', 'dark');
      html.classList.add(prev);
    };
  }, [themeMode]);

  // Inject v4's own PWA manifest + theme-color while this app is mounted.
  // Restores the original ones on unmount so the panel keeps its identity.
  useEffect(() => {
    const head = document.head;
    let manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const prevManifestHref = manifestLink?.href;
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      head.appendChild(manifestLink);
    }
    manifestLink.href = '/client/manifest.json';

    let themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const prevThemeContent = themeMeta?.content;
    if (!themeMeta) {
      themeMeta = document.createElement('meta');
      themeMeta.name = 'theme-color';
      head.appendChild(themeMeta);
    }
    themeMeta.content = accent.primary;

    return () => {
      if (manifestLink && prevManifestHref) manifestLink.href = prevManifestHref;
      if (themeMeta && prevThemeContent) themeMeta.content = prevThemeContent;
    };
  }, [accent.primary]);

  const onSettings = location.pathname.includes('/settings');
  // Home is a minimal launcher - no top header, the firm logo is the header.
  const onHome = location.pathname === '/client' || location.pathname === '/client/';
  const hideHeader = onSettings || onHome;

  // Font scaling is handled centrally in V4SettingsProvider by setting
  // <html>.style.fontSize. Tailwind's rem-based text-* classes pick it up
  // automatically - no wrapper class needed here.

  // CSS variables that drive accent colors throughout v4. Components use
  // bg-[var(--v4-accent)] / text-[var(--v4-accent-text)] etc.
  // The tint flips automatically in dark mode to a low-alpha overlay of the
  // accent so the same class works on both backgrounds.
  const cssVars = {
    fontFamily: font,
    '--v4-accent':       accent.primary,
    '--v4-accent-hover': accent.hover,
    '--v4-accent-text':  themeMode === 'dark' ? accent.primary : accent.text,
    '--v4-accent-tint':  themeMode === 'dark' ? `${accent.primary}24` : accent.tint, // 24 = 14% alpha
    '--v4-accent-soft':  themeMode === 'dark' ? `${accent.primary}33` : `${accent.primary}1A`, // 1A=10%, 33=20%
  } as React.CSSProperties;

  return (
    <div
      className={cn(
        // pt safe-area keeps the header clear of the notch / status bar in the
        // iOS standalone PWA (status bar is black-translucent, so content draws
        // under it otherwise). The padding region shows bg-background.
        'flex flex-col h-screen overflow-hidden bg-background text-foreground pt-[env(safe-area-inset-top)]',
        // v4 owns its own theme - applied to its own root, NOT to <html>.
        // Toggling dark mode here doesn't affect the firm admin panel.
        themeMode === 'dark' && 'dark'
      )}
      // h-screen (100vh) is the fallback; 100dvh tracks the *visual* viewport so
      // the shell never exceeds the screen. A fixed-height flex column with an
      // inner scroll area keeps the bottom nav reliably pinned on iOS standalone
      // PWAs, where document-level `position: fixed` drifts up during momentum
      // scroll on long pages (the "footer floats into the middle" bug).
      style={{ ...cssVars, height: '100dvh' }}
      dir={dir}
    >
      <SplashV4 />
      <InstallPrompt />

      {/* Offline banner */}
      {!isOnline && (
        <div className="shrink-0 bg-amber-500 text-white text-center text-xs font-semibold py-1.5 px-3">
          You're offline - showing the latest data we have
        </div>
      )}

      {/* Update available toast */}
      {updateAvailable && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 bg-foreground text-background rounded-full shadow-xl px-4 py-2 text-xs font-semibold flex items-center gap-3 animate-in slide-in-from-bottom">
          New version ready
          <button onClick={applyUpdate} className="rounded-full bg-[var(--v4-accent)] px-3 py-1 text-white text-[11px] font-bold">
            Reload
          </button>
        </div>
      )}

      {!hideHeader && (
        <header className="shrink-0 border-b border-border/70 bg-card/95 backdrop-blur-md">
          <div className="mx-auto flex max-w-[720px] items-center justify-between px-4 py-2.5">
            {/* Business identity + accountant chip */}
            <div className="flex items-center gap-3 min-w-0">
              {/* RoniDoc icon - square R-with-dots brand mark */}
              <img
                src="/client/icon.png"
                alt="RoniDoc"
                className="h-9 w-9 flex-shrink-0 rounded-lg"
              />
              <div className="flex flex-col leading-tight min-w-0">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t('app_name')}</span>
                <span className="text-[13px] font-semibold truncate">{clientName || t('business_name')}</span>
              </div>
            </div>

            <Link
              to="/client/settings"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors"
              aria-label="Settings"
            >
              <SettingsIcon className="h-4 w-4" />
            </Link>
          </div>
        </header>
      )}

      {/* Inner scroll area - the ONLY scroller. The document body never
          scrolls, so fixed/pinned elements don't drift on iOS standalone. */}
      <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="mx-auto w-full max-w-[720px] flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 pt-4 pb-4">
          <Outlet />
        </div>
      </main>

      {/* Bottom tabs */}
      {!hideHeader && (
        <nav className="shrink-0 border-t border-border bg-card pb-[env(safe-area-inset-bottom)]">
          <div className="mx-auto flex max-w-[720px] items-stretch justify-around">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'relative flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors',
                    isActive ? 'text-[var(--v4-accent-text)]' : 'text-muted-foreground hover:text-foreground'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute top-0 h-0.5 w-10 rounded-b-full bg-indigo-600" />}
                    <item.icon className={cn('h-5 w-5', isActive && 'stroke-[2.25]')} />
                    <span>{t(item.labelKey)}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

export default function ClientPwaV4Layout() {
  return (
    <V4SettingsProvider>
      <PinGate>
        <Shell />
      </PinGate>
    </V4SettingsProvider>
  );
}
